import type { Camera } from "./Camera";
import type { World } from "./World";
import { WORLD_RADIUS } from "./World";
import { drawRosie } from "./sprites/drawRosie";
import type { Rosie } from "./entities/Rosie";
import type { DustBunny } from "./entities/DustBunny";
import { FloorCache } from "./FloorCache";

export class Renderer {
  canvas: HTMLCanvasElement;
  ctx: CanvasRenderingContext2D;
  dpr = window.devicePixelRatio || 1;
  private floor = new FloorCache();

  constructor(canvas: HTMLCanvasElement) {
    this.canvas = canvas;
    const ctx = canvas.getContext("2d");
    if (!ctx) throw new Error("could not get 2d context");
    this.ctx = ctx;
  }

  resize(w: number, h: number): void {
    this.dpr = window.devicePixelRatio || 1;
    this.canvas.width = Math.floor(w * this.dpr);
    this.canvas.height = Math.floor(h * this.dpr);
    this.canvas.style.width = `${w}px`;
    this.canvas.style.height = `${h}px`;
    this.ctx.setTransform(this.dpr, 0, 0, this.dpr, 0, 0);
  }

  draw(world: World, camera: Camera, now: number): void {
    const ctx = this.ctx;
    const w = camera.viewportW;
    const h = camera.viewportH;

    ctx.fillStyle = "#0d0a08";
    ctx.fillRect(0, 0, w, h);

    ctx.save();
    ctx.translate(w / 2 - camera.x, h / 2 - camera.y);

    this.floor.draw(ctx);
    this.drawDust(world.dust, now);
    this.drawTrail(world.player);
    for (const b of world.bots) this.drawTrail(b);
    this.drawRosieEntity(world.player, now);
    for (const b of world.bots) this.drawRosieEntity(b, now);

    ctx.restore();

    this.drawMinimap(world, camera);
  }

  private drawDust(dust: DustBunny[], now: number): void {
    const ctx = this.ctx;
    for (const d of dust) {
      const pulse = 1 + Math.sin(now / 400 + d.seed) * 0.08;
      const colors = colorsForValue(d.value);
      // fuzzy outer halo
      ctx.fillStyle = colors.halo;
      ctx.beginPath();
      ctx.arc(d.x, d.y, d.r * 1.7 * pulse, 0, Math.PI * 2);
      ctx.fill();
      // body
      ctx.fillStyle = colors.body;
      ctx.beginPath();
      ctx.arc(d.x, d.y, d.r * 0.7 * pulse, 0, Math.PI * 2);
      ctx.fill();
      // sparkly highlight for the big ones
      if (d.value >= 3) {
        ctx.fillStyle = colors.highlight;
        ctx.beginPath();
        ctx.arc(d.x - d.r * 0.2, d.y - d.r * 0.2, d.r * 0.25 * pulse, 0, Math.PI * 2);
        ctx.fill();
      }
    }
  }

  private drawTrail(r: Rosie): void {
    const ctx = this.ctx;
    const spacing = 6;
    const visible = Math.min(r.trail.length, r.trailLength * spacing);
    const baseSize = 8 * r.scale;
    for (let i = visible - 1; i >= 0; i -= spacing) {
      const p = r.trail[i];
      if (!p) continue;
      const size = baseSize + Math.sin(i * 0.4) * 0.4;
      ctx.fillStyle = r.accent;
      ctx.globalAlpha = 0.16;
      ctx.beginPath();
      ctx.arc(p.x, p.y, size * 1.7, 0, Math.PI * 2);
      ctx.fill();
      ctx.globalAlpha = r.alive ? 0.95 : 0.35;
      ctx.beginPath();
      ctx.arc(p.x, p.y, size, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = "rgba(0,0,0,0.4)";
      ctx.globalAlpha = r.alive ? 0.6 : 0.2;
      ctx.beginPath();
      ctx.arc(p.x, p.y - size * 0.6, size * 0.25, 0, Math.PI * 2);
      ctx.fill();
    }
    ctx.globalAlpha = 1;
  }

  private drawRosieEntity(r: Rosie, now: number): void {
    if (!r.alive) return;
    drawRosie(this.ctx, r.x, r.y, r.heading, 1.2 * r.scale, {
      accent: r.accent,
      isPlayer: r.isPlayer,
      isInvincible: r.isInvincible(now),
      now,
    });
  }

  private drawMinimap(world: World, camera: Camera): void {
    const ctx = this.ctx;
    const size = Math.min(140, Math.max(90, camera.viewportW * 0.18));
    const pad = 12;
    const cx = camera.viewportW - pad - size / 2;
    const cy = pad + size / 2;
    const scale = size / 2 / WORLD_RADIUS;

    ctx.save();
    ctx.fillStyle = "rgba(10,12,16,0.6)";
    ctx.strokeStyle = "rgba(111,214,200,0.4)";
    ctx.lineWidth = 1.5;
    ctx.beginPath();
    ctx.arc(cx, cy, size / 2, 0, Math.PI * 2);
    ctx.fill();
    ctx.stroke();

    for (const b of world.bots) {
      if (!b.alive) continue;
      ctx.fillStyle = b.accent;
      ctx.globalAlpha = 0.85;
      ctx.beginPath();
      ctx.arc(cx + b.x * scale, cy + b.y * scale, 2.4 * b.scale, 0, Math.PI * 2);
      ctx.fill();
    }
    ctx.fillStyle = world.player.accent;
    ctx.globalAlpha = 1;
    ctx.beginPath();
    ctx.arc(
      cx + world.player.x * scale,
      cy + world.player.y * scale,
      3.5 * world.player.scale,
      0,
      Math.PI * 2,
    );
    ctx.fill();
    ctx.restore();
  }
}

function colorsForValue(v: number): { halo: string; body: string; highlight: string } {
  if (v >= 8) {
    return {
      halo: "rgba(255,210,90,0.40)",
      body: "#ffd25a",
      highlight: "rgba(255,255,220,0.95)",
    };
  }
  if (v >= 3) {
    return {
      halo: "rgba(255,236,180,0.35)",
      body: "#ffe7a3",
      highlight: "rgba(255,255,235,0.9)",
    };
  }
  if (v >= 2) {
    return {
      halo: "rgba(220,225,235,0.32)",
      body: "#e8edf5",
      highlight: "rgba(255,255,255,0)",
    };
  }
  return {
    halo: "rgba(200,210,220,0.28)",
    body: "#dbe2ec",
    highlight: "rgba(255,255,255,0)",
  };
}
